import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { CalendarDays, Pencil } from 'lucide-react';
import {
  type BoardMember,
  WORKSPACE_TABLE_BOARD_LIMIT,
  type Board,
  type Label,
  type PublicProfile,
  type WorkspaceTable,
  type WorkspaceTableRow,
} from '@molt/shared';
import {
  AvatarLabelsProvider,
  AvatarStack,
  Button,
  EmptyState,
  IconButton,
  LabelChip,
  TextField,
  profileName,
} from '@/components';
import { uniqueProfiles } from '@/lib/avatar-labels';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  nextTableSort,
  setWorkspaceTableKeyword,
  sortedWorkspaceTableRows,
  toggleWorkspaceTableFilterValue,
  type BoardTableSort,
  type WorkspaceTableFilterSelection,
} from '@/lib/workspace-table';

export interface WorkspaceTableListOption {
  readonly id: string;
  readonly title: string;
  readonly boardId: string;
  readonly boardTitle: string;
}

export interface WorkspaceTableMemberOption extends BoardMember {
  readonly boardId: string;
}

export interface WorkspaceTableMoveIntent {
  readonly cardId: string;
  readonly boardId: string;
  readonly fromListId: string;
  readonly toListId: string;
}

export interface WorkspaceTablePageProps {
  readonly workspaceName: string;
  readonly boards: readonly Board[];
  readonly selectedBoardIds: readonly string[];
  readonly editableBoardIds: readonly string[];
  readonly filter: WorkspaceTableFilterSelection;
  readonly members: readonly PublicProfile[];
  readonly boardMembers: readonly WorkspaceTableMemberOption[];
  readonly labels: readonly Label[];
  readonly lists: readonly WorkspaceTableListOption[];
  readonly table: WorkspaceTable | undefined;
  readonly loadingBoards?: boolean | undefined;
  readonly loadingFilters?: boolean | undefined;
  readonly loadingTable?: boolean | undefined;
  readonly configurationLocked?: boolean | undefined;
  readonly error?: string | undefined;
  readonly onBoardSelectionChange: (boardIds: readonly string[]) => void;
  readonly onFilterChange: (filter: WorkspaceTableFilterSelection) => void;
  readonly onRetry: () => void;
  readonly onOpenCard?:
    | ((card: {
        shortLink: string;
        cardNumber: number;
        ref: string | null;
        title: string;
      }) => void)
    | undefined;
  readonly onRenameCard?:
    | ((cardId: string, boardId: string, listId: string, title: string) => void)
    | undefined;
  readonly onMoveCard?: ((intent: WorkspaceTableMoveIntent) => void) | undefined;
  readonly onSetDueDate?:
    ((cardId: string, boardId: string, dueAt: string | null) => void) | undefined;
  readonly onAddLabel?:
    ((cardId: string, boardId: string, labelId: string) => void) | undefined;
  readonly onRemoveLabel?:
    ((cardId: string, boardId: string, labelId: string) => void) | undefined;
  readonly onAddMember?:
    ((cardId: string, boardId: string, userId: string) => void) | undefined;
  readonly onRemoveMember?:
    ((cardId: string, boardId: string, userId: string) => void) | undefined;
}

export function WorkspaceTablePage({
  workspaceName,
  boards,
  selectedBoardIds,
  editableBoardIds,
  filter,
  members,
  boardMembers,
  labels,
  lists,
  table,
  loadingBoards = false,
  loadingFilters = false,
  loadingTable = false,
  configurationLocked = false,
  error,
  onBoardSelectionChange,
  onFilterChange,
  onRetry,
  onOpenCard,
  onRenameCard,
  onMoveCard,
  onSetDueDate,
  onAddLabel,
  onRemoveLabel,
  onAddMember,
  onRemoveMember,
}: WorkspaceTablePageProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [sort, setSort] = useState<BoardTableSort | null>(null);
  const [renameCardId, setRenameCardId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const rows = useMemo(
    () => sortedWorkspaceTableRows(table?.rows ?? [], sort),
    [sort, table?.rows],
  );
  const selected = new Set(selectedBoardIds);
  const editable = useMemo(() => new Set(editableBoardIds), [editableBoardIds]);
  /*
   * The avatars this page can draw: the workspace's members, every selected
   * board's members and every row's card members. Clashing initials are
   * separated across that whole set, so one person draws one label in every
   * row of the table, whichever board the row belongs to.
   */
  const avatarRoster = uniqueProfiles([
    members,
    boardMembers,
    ...(table?.rows ?? []).map((row) => row.members),
  ]);

  function toggleBoard(boardId: string): void {
    if (configurationLocked) return;
    const next = selected.has(boardId)
      ? selectedBoardIds.filter((id) => id !== boardId)
      : [...selectedBoardIds, boardId].slice(0, WORKSPACE_TABLE_BOARD_LIMIT);
    onBoardSelectionChange(next);
  }

  function submitRename(event: FormEvent<HTMLFormElement>, row: WorkspaceTableRow): void {
    event.preventDefault();
    const title = renameDraft.trim();
    if (title !== '' && title !== row.card.title) {
      onRenameCard?.(row.card.id, row.board.id, row.list.id, title);
    }
    setRenameCardId(null);
    setRenameDraft('');
  }

  return (
    <AvatarLabelsProvider roster={avatarRoster}>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-8 sm:px-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
              {t('workspaceViews.table.title')}
            </h1>
            <p className="text-card text-ink-muted">{workspaceName}</p>
          </div>
          <p role="status" className="text-card text-ink-muted">
            {/* ⚠ `t`, not `t(…, …, count)` — the noun agrees with the CAP, so
              `1 of 20 boards selected` is correct at every arity. */}
            {t('workspaceViews.table.selectedCount', {
              count: selectedBoardIds.length,
              limit: WORKSPACE_TABLE_BOARD_LIMIT,
            })}
          </p>
        </header>

        <section
          aria-labelledby="workspace-table-board-selection"
          className="rounded-card border border-border-strong bg-panel p-3"
        >
          <h2
            id="workspace-table-board-selection"
            className="text-sm font-semibold text-ink"
          >
            {t('workspaceViews.table.boardsHeading')}
          </h2>
          {boards.length === 0 && !loadingBoards ? (
            <EmptyState
              tone="neutral"
              size="inline"
              title={t('workspaceViews.table.noBoards')}
            />
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {boards.map((board) => {
                const checked = selected.has(board.id);
                const disabled =
                  configurationLocked ||
                  (!checked && selectedBoardIds.length >= WORKSPACE_TABLE_BOARD_LIMIT);
                return (
                  <label
                    key={board.id}
                    className="flex items-center gap-2 rounded-card border border-border bg-surface px-2 py-1 text-card"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleBoard(board.id)}
                    />
                    <span>{board.title}</span>
                  </label>
                );
              })}
            </div>
          )}
        </section>

        <section
          aria-labelledby="workspace-table-filters"
          className="grid gap-3 rounded-card border border-border-strong bg-panel p-3 md:grid-cols-5"
        >
          <h2 id="workspace-table-filters" className="sr-only">
            {t('workspaceViews.table.filtersHeading')}
          </h2>
          <TextField
            label={t('workspaceViews.table.keywordLabel')}
            value={filter.keyword}
            disabled={configurationLocked}
            onChange={(event) =>
              onFilterChange(setWorkspaceTableKeyword(filter, event.currentTarget.value))
            }
          />
          <FacetGroup
            title={t('workspaceViews.table.facet.members')}
            busy={loadingFilters}
            options={[
              { id: 'none', label: t('workspaceViews.table.noMembersOption') },
              ...members.map((member) => ({ id: member.id, label: profileName(member) })),
            ]}
            selected={filter.members}
            disabled={configurationLocked}
            onToggle={(id) =>
              onFilterChange(toggleWorkspaceTableFilterValue(filter, 'members', id))
            }
          />
          <FacetGroup
            title={t('workspaceViews.table.facet.labels')}
            busy={loadingFilters}
            options={[
              { id: 'none', label: t('workspaceViews.table.noLabelsOption') },
              ...labels.map((label) => ({
                id: label.id,
                label: label.name ?? t('workspaceViews.table.unnamedLabel'),
                labelNode: (
                  <LabelChip colour={label.colour} name={label.name} size="sm" />
                ),
              })),
            ]}
            selected={filter.labels}
            disabled={configurationLocked}
            onToggle={(id) =>
              onFilterChange(toggleWorkspaceTableFilterValue(filter, 'labels', id))
            }
          />
          <FacetGroup
            title={t('workspaceViews.table.facet.lists')}
            busy={loadingFilters}
            options={lists.map((list) => ({
              id: list.id,
              label: t('workspaceViews.table.listOption', {
                board: list.boardTitle,
                list: list.title,
              }),
            }))}
            selected={filter.lists}
            disabled={configurationLocked}
            onToggle={(id) =>
              onFilterChange(toggleWorkspaceTableFilterValue(filter, 'lists', id))
            }
          />
          <FacetGroup
            title={t('workspaceViews.table.facet.due')}
            options={[
              /* ⚠ The SHORT due spelling, preserved byte-for-byte. The workspace
               views index and the Workspace Calendar render the LONG form
               (`Due in the next week`) through `boardFace.filters.due.*`;
               converging them would be a copy change, not a routing fix. The
               `id`s are WIRE values and stay literal. */
              { id: 'no_due_date', label: t('workspaceViews.table.due.no_due_date') },
              { id: 'overdue', label: t('workspaceViews.table.due.overdue') },
              { id: 'due_next_day', label: t('workspaceViews.table.due.due_next_day') },
              { id: 'due_next_week', label: t('workspaceViews.table.due.due_next_week') },
              {
                id: 'due_next_month',
                label: t('workspaceViews.table.due.due_next_month'),
              },
            ]}
            selected={filter.due}
            disabled={configurationLocked}
            onToggle={(id) =>
              onFilterChange(toggleWorkspaceTableFilterValue(filter, 'due', id))
            }
          />
        </section>

        {error !== undefined ? (
          <EmptyState
            tone="error"
            size="panel"
            title={t('workspaceViews.table.loadFailedTitle')}
            body={error}
            action={<Button onClick={onRetry}>{t('workspaceViews.table.retry')}</Button>}
          />
        ) : loadingTable ? (
          <div
            aria-busy="true"
            className="rounded-card border border-border-strong bg-panel p-4 text-card"
          >
            {t('workspaceViews.table.loading')}
          </div>
        ) : selectedBoardIds.length === 0 ? (
          <EmptyState
            tone="neutral"
            size="panel"
            title={t('workspaceViews.table.selectBoard')}
          />
        ) : (
          <section
            aria-label={t('workspaceViews.table.rowsLabel')}
            className="overflow-auto rounded-card border border-border-strong bg-panel"
          >
            <table className="min-w-[64rem] w-full border-collapse text-left text-card">
              <thead className="sticky top-0 bg-surface-raised text-xs uppercase text-ink-muted">
                <tr>
                  <th className="px-2 py-2">{t('workspaceViews.table.column.board')}</th>
                  <SortableHeading
                    label={t('workspaceViews.table.column.card')}
                    sortKey="card"
                    sort={sort}
                    onSort={(key) => setSort((current) => nextTableSort(current, key))}
                  />
                  <SortableHeading
                    label={t('workspaceViews.table.column.list')}
                    sortKey="list"
                    sort={sort}
                    onSort={(key) => setSort((current) => nextTableSort(current, key))}
                  />
                  <th className="px-2 py-2">{t('workspaceViews.table.column.labels')}</th>
                  <th className="px-2 py-2">
                    {t('workspaceViews.table.column.members')}
                  </th>
                  <SortableHeading
                    label={t('workspaceViews.table.column.dueDate')}
                    sortKey="due"
                    sort={sort}
                    onSort={(key) => setSort((current) => nextTableSort(current, key))}
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.card.id} className="border-t border-border align-top">
                    <td className="px-2 py-2 font-medium text-ink">{row.board.title}</td>
                    <td className="px-2 py-2">
                      {renameCardId === row.card.id ? (
                        <form
                          className="flex items-center gap-1"
                          onSubmit={(event) => submitRename(event, row)}
                        >
                          <TextField
                            label={t('workspaceViews.table.renameCard', {
                              title: row.card.title,
                            })}
                            value={renameDraft}
                            onChange={(event) =>
                              setRenameDraft(event.currentTarget.value)
                            }
                          />
                          <Button type="submit" size="sm" variant="primary">
                            {t('workspaceViews.table.save')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="quiet"
                            onClick={() => setRenameCardId(null)}
                          >
                            {t('workspaceViews.table.cancel')}
                          </Button>
                        </form>
                      ) : (
                        <div className="flex min-w-0 items-center gap-1">
                          <button
                            type="button"
                            className="min-w-0 truncate text-left font-medium text-ink underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            onClick={() => onOpenCard?.(row.card)}
                          >
                            {row.card.title}
                          </button>
                          {editable.has(row.board.id) ? (
                            <IconButton
                              label={t('workspaceViews.table.renameCard', {
                                title: row.card.title,
                              })}
                              size="sm"
                              onClick={() => {
                                setRenameCardId(row.card.id);
                                setRenameDraft(row.card.title);
                              }}
                            >
                              <Pencil aria-hidden="true" className="size-3.5" />
                            </IconButton>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {editable.has(row.board.id) ? (
                        <select
                          aria-label={t('workspaceViews.table.changeList', {
                            title: row.card.title,
                          })}
                          className="h-8 w-full min-w-40 rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
                          value={row.list.id}
                          onChange={(event) =>
                            onMoveCard?.({
                              cardId: row.card.id,
                              boardId: row.board.id,
                              fromListId: row.list.id,
                              toListId: event.currentTarget.value,
                            })
                          }
                        >
                          {lists
                            .filter((list) => list.boardId === row.board.id)
                            .map((list) => (
                              <option key={list.id} value={list.id}>
                                {list.title}
                              </option>
                            ))}
                        </select>
                      ) : (
                        row.list.title
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {editable.has(row.board.id) ? (
                        <FacetGroup
                          title={t('workspaceViews.table.editLabels', {
                            title: row.card.title,
                          })}
                          options={labels
                            .filter((label) => label.boardId === row.board.id)
                            .map((label) => ({
                              id: label.id,
                              label: label.name ?? t('workspaceViews.table.unnamedLabel'),
                              labelNode: (
                                <LabelChip
                                  colour={label.colour}
                                  name={label.name}
                                  size="sm"
                                />
                              ),
                            }))}
                          selected={row.labels.map((label) => label.id)}
                          onToggle={(labelId) =>
                            row.labels.some((label) => label.id === labelId)
                              ? onRemoveLabel?.(row.card.id, row.board.id, labelId)
                              : onAddLabel?.(row.card.id, row.board.id, labelId)
                          }
                        />
                      ) : null}
                      <div className="flex flex-wrap gap-1">
                        {row.labels.map((label) => (
                          <LabelChip
                            key={label.id}
                            colour={label.colour}
                            name={label.name}
                            size="sm"
                          />
                        ))}
                        {row.labels.length === 0 ? (
                          <span className="text-ink-muted">
                            {t('workspaceViews.table.noLabelsCell')}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      {editable.has(row.board.id) ? (
                        <FacetGroup
                          title={t('workspaceViews.table.editMembers', {
                            title: row.card.title,
                          })}
                          options={boardMembers
                            .filter((member) => member.boardId === row.board.id)
                            .map((member) => ({
                              id: member.id,
                              label: profileName(member),
                            }))}
                          selected={row.members.map((member) => member.id)}
                          onToggle={(userId) =>
                            row.members.some((member) => member.id === userId)
                              ? onRemoveMember?.(row.card.id, row.board.id, userId)
                              : onAddMember?.(row.card.id, row.board.id, userId)
                          }
                        />
                      ) : null}
                      {row.members.length === 0 ? (
                        <span className="text-ink-muted">
                          {t('workspaceViews.table.noMembersCell')}
                        </span>
                      ) : (
                        <AvatarStack
                          members={row.members}
                          size="xs"
                          max={5}
                          overflowAction="none"
                          names="tooltip"
                        />
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {editable.has(row.board.id) ? (
                        <label className="flex items-center gap-2">
                          <CalendarDays
                            aria-hidden="true"
                            className="size-4 text-ink-muted"
                          />
                          <input
                            aria-label={t('workspaceViews.table.setDueDate', {
                              title: row.card.title,
                            })}
                            type="date"
                            value={row.card.dueAt?.slice(0, 10) ?? ''}
                            className="h-8 rounded-card border border-border-strong bg-surface px-2 text-card text-ink"
                            onChange={(event) =>
                              onSetDueDate?.(
                                row.card.id,
                                row.board.id,
                                event.currentTarget.value === ''
                                  ? null
                                  : `${event.currentTarget.value}T17:00:00.000Z`,
                              )
                            }
                          />
                        </label>
                      ) : (
                        dueDateLabel(row.card.dueAt, t('workspaceViews.table.noDueDate'))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </AvatarLabelsProvider>
  );
}

/**
 * ⚠ Takes the RESOLVED `No due date` string as a parameter so the module stays
 * hook-free. It must not return a catalogue KEY: `lib/comments.ts` did exactly
 * that and poisoned a search haystack with key text. The non-null arm formats
 * from `Intl.DateTimeFormat(undefined, …)` — the BROWSER locale, which is
 * independent of the interface language; only the null arm is copy.
 */
function dueDateLabel(value: string | null, noDueDate: string): string {
  if (value === null) return noDueDate;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
    new Date(value),
  );
}

interface FacetGroupProps {
  readonly title: string;
  readonly options: readonly {
    readonly id: string;
    readonly label: string;
    readonly labelNode?: ReactElement | undefined;
  }[];
  readonly selected: readonly string[];
  readonly busy?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  readonly onToggle: (id: string) => void;
}

function FacetGroup({
  title,
  options,
  selected,
  busy = false,
  disabled = false,
  onToggle,
}: FacetGroupProps): ReactElement {
  const { t } = useInterfaceLanguage();

  function toggle(id: string): void {
    if (disabled) return;
    onToggle(id);
  }

  return (
    <details className="rounded-card border border-border bg-surface p-2">
      <summary className="cursor-pointer text-sm font-semibold text-ink">{title}</summary>
      <div className="mt-2 flex max-h-40 flex-col gap-1 overflow-y-auto">
        {busy ? (
          <p className="text-card text-ink-muted">
            {t('workspaceViews.table.facetLoading')}
          </p>
        ) : null}
        {options.map((option) => (
          <label key={option.id} className="flex items-center gap-2 text-card">
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              disabled={disabled}
              onChange={() => toggle(option.id)}
            />
            {option.labelNode ?? <span>{option.label}</span>}
          </label>
        ))}
      </div>
    </details>
  );
}

interface SortableHeadingProps {
  readonly label: string;
  readonly sortKey: 'card' | 'list' | 'due';
  readonly sort: BoardTableSort | null;
  readonly onSort: (key: 'card' | 'list' | 'due') => void;
}

function SortableHeading({
  label,
  sortKey,
  sort,
  onSort,
}: SortableHeadingProps): ReactElement {
  const active = sort?.key === sortKey;
  return (
    <th
      scope="col"
      aria-sort={
        active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      className="px-2 py-2"
    >
      <button
        type="button"
        className="font-semibold hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        onClick={() => onSort(sortKey)}
      >
        {label}
        {active ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );
}
